import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Badge,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import { getNotificationType, Notification as NotificationType } from "../../../utils/commonApis";
import PurchaseOrderNotification from "./notifications/PurchaseOrderNotification";
import WastageIncreaseNotification from "./notifications/WastageIncreaseNotification";
import WastageDecreaseNotification from "./notifications/WastageDecreaseNotification";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notification-tabpanel-${index}`}
      aria-labelledby={`notification-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `notification-tab-${index}`,
    "aria-controls": `notification-tabpanel-${index}`,
  };
}

const NotificationComponent: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [openNotifications, setOpenNotifications] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    // Placeholder for fetching notifications
    // Will be implemented later
    setNotifications([]);
  }, []);

  const toggleNotifications = () => {
    setOpenNotifications(!openNotifications);
  };

  // Group notifications by type
  const purchaseOrderNotifications = notifications.filter(
    (notification) => getNotificationType(notification.DataDump) === "Accept Purchase Order"
  );
  
  const wastageIncreaseNotifications = notifications.filter(
    (notification) => getNotificationType(notification.DataDump) === "Wastage Increase"
  );
  
  const wastageDecreaseNotifications = notifications.filter(
    (notification) => getNotificationType(notification.DataDump) === "Wastage Decrease"
  );

  return (
    <Box sx={{ position: "relative" }}>
      <IconButton onClick={toggleNotifications} color="inherit">
        <Badge badgeContent={notifications.length} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      {openNotifications && (
        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            left: 0,
            top: 40,
            width: { xs: 300, sm: 400, md: 500 },
            maxHeight: 500,
            overflowY: "auto",
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              bgcolor: "primary.main",
              color: "white",
            }}
          >
            <Typography variant="h6">Notifications</Typography>
            <IconButton onClick={toggleNotifications} color="inherit">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          ) : notifications.length === 0 ? (
            <Typography sx={{ p: 3, textAlign: "center" }}>
              No notifications to display.
            </Typography>
          ) : (
            <>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="notification tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="All" {...a11yProps(0)} />
                  <Tab 
                    label={`Purchase Orders (${purchaseOrderNotifications.length})`} 
                    {...a11yProps(1)} 
                  />
                  <Tab 
                    label={`Wastage Increase (${wastageIncreaseNotifications.length})`} 
                    {...a11yProps(2)} 
                  />
                  <Tab 
                    label={`Wastage Decrease (${wastageDecreaseNotifications.length})`} 
                    {...a11yProps(3)} 
                  />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <List>
                  {notifications.map((notification) => {
                    const notificationType = getNotificationType(notification.DataDump);
                    return renderNotificationItem(notification, notificationType);
                  })}
                </List>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <List>
                  {purchaseOrderNotifications.map((notification) => (
                    <PurchaseOrderNotification key={notification.NotificationId} notification={notification} />
                  ))}
                </List>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <List>
                  {wastageIncreaseNotifications.map((notification) => (
                    <WastageIncreaseNotification key={notification.NotificationId} notification={notification} />
                  ))}
                </List>
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <List>
                  {wastageDecreaseNotifications.map((notification) => (
                    <WastageDecreaseNotification key={notification.NotificationId} notification={notification} />
                  ))}
                </List>
              </TabPanel>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

// Simple function to render notification items by type
const renderNotificationItem = (notification: NotificationType, type: string) => {
  switch (type) {
    case "Accept Purchase Order":
      return <PurchaseOrderNotification notification={notification} />;
    case "Wastage Increase":
      return <WastageIncreaseNotification notification={notification} />;
    case "Wastage Decrease":
      return <WastageDecreaseNotification notification={notification} />;
    default:
      return (
        <ListItem key={notification.NotificationId} divider>
          <ListItemText
            primary={`Unknown Notification Type`}
            secondary={`Created at: ${new Date(notification.CreatedAt * 1000).toLocaleString()}`}
          />
        </ListItem>
      );
  }
};

export default NotificationComponent;